import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { userMessages } from 'src/features/users/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { generalErrorMessages } from 'src/utils/messages';
import {
    GET_PARKINGS_MEMBERSHIP_RESOURCE,
    useGetParkingsMembership,
} from 'src/features/users/api/useGetParkingsMembership';
import { Membership } from 'src/features/users/utils/types';
import { LIST_PARKING_MEMBERSHIPS_RESOURCE } from 'src/features/users/api/useListParkingsMemberships';
import { useUpdateParkingMembership } from 'src/features/users/api/useUpdateParkingMembership';
import { userFormSchema, UserFormSchemaValues } from 'src/features/users/utils/formSchema';
import UpdateUserForm from 'src/features/users/components/forms/UpdateUserForm';

interface UserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembership: Membership;
    children?: React.ReactNode;
}

function UpdateUserFormModal<TData>({ semantics, selectedMembership, children }: UserFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { mutate, isPending } = useUpdateParkingMembership({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [GET_PARKINGS_MEMBERSHIP_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_PARKING_MEMBERSHIPS_RESOURCE] });
            toast({
                title: formatMessage(userMessages.updateUserSuccess),
                description: formatMessage(userMessages.updateUserSuccessDescription, {
                    entry: success.user.name,
                }),
            });
            setIsModalOpen(false);
        },
        onError: () => {
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: formatMessage(generalErrorMessages.unknownError),
                variant: 'destructive',
            });
        },
    });

    const { data: membershipUser } = useGetParkingsMembership({
        membershipId: selectedMembership?.id ?? '',
        parkingId,
        config: { enabled: !!selectedMembership?.id && isModalOpen },
    });

    const defaultValues = React.useMemo(() => {
        return {
            user_first_name: membershipUser?.user.first_name ?? undefined,
            user_last_name: membershipUser?.user.last_name ?? undefined,
            sp_role: membershipUser?.sp_role?.id ?? undefined,
        };
    }, [membershipUser]);

    const form = useForm<UserFormSchemaValues>({
        resolver: zodResolver(userFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const title = formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() });

    const onSubmit = React.useCallback(
        (data: UserFormSchemaValues) => mutate({ membershipId: selectedMembership.id, parkingId, ...data }),
        [mutate, selectedMembership, parkingId],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(userMessages.editUserSubtitle, { entry: selectedMembership.user.name })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <UpdateUserForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || !form.formState.isDirty}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default UpdateUserFormModal;
